import { useState, useEffect, useMemo, useRef } from "react";
import { useParams } from "react-router-dom";
import { Form, Button, Row, Col, Table, Card, Nav } from "react-bootstrap";
import Message from "../../Components/Message";
import Loader from "../../Components/Loader";
import FormContainer from "../../Components/FormContainer";
import BackButton from "../../Components/BackButton";
import { toast } from "react-toastify";
import {
  useUpdateProductMutation,
  useGetProductDetailsQuery,
  useUploadImageMutation,
  useDeleteImageMutation,
  useAdminListPartCategoriesQuery,
} from "../../slices/productsApiSlice";
import FitmentEditor from "../../Components/FitmentEditor";
import WarehouseLocationBuilder from "../../Components/admin/WarehouseLocationBuilder";
import {
  useAdminGetProductTierPricesQuery,
  useAdminUpsertProductTierPricesMutation,
} from "../../slices/partnerCodesApiSlice";
import { normalizeMaterial } from "../../utils/material";
import { PRODUCT_STATUS_OPTIONS } from "../../utils/productStatus";
import { isRotorOrDiscCategory, normalizeCoating } from "../../utils/coating";
import { PRODUCT_DIMENSION_FORMATS } from "../../utils/productDimension";
import EngineOilProductFields, { buildEngineOilAttributes } from "../../Components/admin/EngineOilProductFields";
import { buildAdminCategoryOptions, getAdminCategorySelectValue } from "../../utils/partCategoryOptions";

const MATERIAL_OPTIONS = ["", "Copper enhanced Ceramic", "Copper free ceramic"];
const COATING_OPTIONS = ["Non-coated", "Coated"];
const DRIVETRAIN_OPTIONS = ["", "FWD", "RWD", "AWD", "4WD", "Fits All Drivetrains"];
const YES_NO_OPTIONS = ["", "Yes", "No"];
const ROTOR_COATING_TYPE_OPTIONS = [
  "",
  "Ceramic Metal Composite",
  "Tungsten Carbide Coating",
  "Geomet / Zinc-Aluminum Flake",
  "Zinc Plated",
  "E-Coated",
  "Other",
];
const ROTOR_CONSTRUCTION_OPTIONS = [
  { value: "", label: "Select construction" },
  { value: "Solid", label: "Solid" },
  { value: "Vented", label: "Vented" },
];
const BRAKE_SPEC_TYPE_OPTIONS = [
  { value: "", label: "Auto" },
  { value: "pad", label: "Brake Pad" },
  { value: "rotor", label: "Brake Rotor / Disc" },
  { value: "none", label: "None" },
];
const normalizeBrakeText = (...vals) => vals.map((v) => String(v || "").toLowerCase()).join(" ");
const isBrakePadProduct = (product = {}) => {
  const text = normalizeBrakeText(product.category, product.type, product.name, product.title, product.sku);
  return /brake\s*pads?/.test(text) || /\bkcc[-\s]?bp\b/.test(text) || /\bbp[-\s]?\d/.test(text);
};
const isBrakeRotorProduct = (product = {}) => {
  const text = normalizeBrakeText(product.category, product.type, product.name, product.title, product.sku);
  return /brake\s*(rotors?|discs?)/.test(text) || /\brotors?\b/.test(text) || /\bdiscs?\b/.test(text) || /\bkcc[-\s]?(bd|br)\b/.test(text);
};
const isNonCoated = (value) => {
  const normalized = String(value || "").trim().toLowerCase();
  return ["non-coated", "non coated", "none", "no", "not coated", "uncoated"].includes(normalized);
};
const isEngineOilCategory = (value) => ['engine-oil', 'engine oil'].includes(String(value || '').trim().toLowerCase());
const blankInventoryLocation = () => ({
  barcode: "",
  room: "",
  section: "",
  shelf: "",
  column: "",
  bin: "",
  quantity: "",
});

const ProductEditScreen = () => {
  const { id: productId } = useParams();

  // Core fields
  const [title, setTitle] = useState("");
  const [price, setPrice] = useState(0);
  const [basePrice, setBasePrice] = useState("");
  const [status, setStatus] = useState('active');
  const [salePrice, setSalePrice] = useState('');
  const [saleStartAt, setSaleStartAt] = useState('');
  const [saleEndAt, setSaleEndAt] = useState('');
  const [image, setImage] = useState("");
  const [images, setImages] = useState([]); // extra images
  const [imageCaptions, setImageCaptions] = useState({});
  const [brand, setBrand] = useState("");
  const [category, setCategory] = useState("");

  const [stockQty, setStockQty] = useState(0);        // shelf (storefront stock)
  const [inventoryQty, setInventoryQty] = useState(0); // backroom
  const [description, setDescription] = useState("");


  // Auto-parts fields
  const [sku, setSku] = useState("");
  const [barcode, setBarcode] = useState("");
  const [barcodeScanValue, setBarcodeScanValue] = useState("");
  const [location, setLocation] = useState("");
  const [inventoryLocations, setInventoryLocations] = useState([blankInventoryLocation()]);
  const [partNumber, setPartNumber] = useState("");
  const [oemNumbers, setOemNumbers] = useState(""); // comma-separated
  const [position, setPosition] = useState("none");
  const [axle, setAxle] = useState("none");
  const [material, setMaterial] = useState("");
  const [coating, setCoating] = useState("Non-coated");
  const [packageDimension, setPackageDimension] = useState("");
  const [packageDimensionUnit, setPackageDimensionUnit] = useState("cm");
  const [productDimensionUnit, setProductDimensionUnit] = useState("mm");
  const [productDimensionFormat, setProductDimensionFormat] = useState("length_width_height");
  const [productDimensionValues, setProductDimensionValues] = useState({
    length: "",
    width: "",
    height: "",
    diameter: "",
    thickness: "",
  });
  const [productDimensionCustomText, setProductDimensionCustomText] = useState("");
  const [packageWeight, setPackageWeight] = useState("");
  const [packageWeightUnit, setPackageWeightUnit] = useState("kg");
  const [attributesText, setAttributesText] = useState(""); // JSON or key=value;key2=value2
  const [engineOilAttributes, setEngineOilAttributes] = useState({});
  const [fitments, setFitments] = useState([]);
  const [interchangeables, setInterchangeables] = useState([]);
  const [brakeRotorSpecs, setBrakeRotorSpecs] = useState({
    outerDiameterMm: "",
    outerDiameterIn: "",
    overallHeightMm: "",
    nominalThicknessMm: "",
    minimumThicknessMm: "",
    centerBoreMm: "",
    boltCircleDiameterMm: "",
    manufacturerDimensionText: "",
    rustResistantCoated: "",
    coatingType: "",
    coatingTypeOther: "",
    studLugHoleQuantity: "",
    rotorConstruction: "",
    drivetrainType: "",
    rotorSizeLabel: "",
  });
  const [brakePadSpecs, setBrakePadSpecs] = useState({
    drivetrainType: "",
    padWearSensorIncluded: "",
    mountingHardwareIncluded: "",
    brakeLubricantIncluded: "",
  });
  const [brakeSpecType, setBrakeSpecType] = useState("");


  const {
    data: product,
    isLoading,
    error,
  } = useGetProductDetailsQuery(productId);

  const [updateProduct, { isLoading: loadingUpdate }] = useUpdateProductMutation();
  const [uploadProductImage, { isLoading: loadingUpload }] =useUploadImageMutation();
  const [deleteImage, { isLoading: loadingDelete }] = useDeleteImageMutation();
  const {
    data: partCategories = [],
    isLoading: loadingCategories,
    error: categoriesError,
  } = useAdminListPartCategoriesQuery();

  const {
    data: tierPricesData,
    isLoading: loadingTierPrices,
    error: tierPricesError,
  } = useAdminGetProductTierPricesQuery(productId, {
    skip: !productId,
  });
  const [upsertTierPrices, { isLoading: savingTierPrices }] = useAdminUpsertProductTierPricesMutation();

  const [tierPriceRows, setTierPriceRows] = useState([]);
  const [tierPricesDirty, setTierPricesDirty] = useState(false);
  const [activeEditorTab, setActiveEditorTab] = useState("details");
  const barcodeScanInputRef = useRef(null);

  const categoryOptions = useMemo(() => {
    return buildAdminCategoryOptions(partCategories, category);
  }, [partCategories, category]);
  const categorySelectValue = useMemo(
    () => getAdminCategorySelectValue(categoryOptions, category),
    [categoryOptions, category]
  );

  useEffect(() => {
    if (!product) return;

    // Core
    setTitle(product.title || product.name || "");
    setPrice(Number(product.price ?? 0));
    setBasePrice(product.basePrice === null || product.basePrice === undefined ? "" : String(product.basePrice));
    setStatus(product.status || 'active');
    setSalePrice(
     product.salePrice === null || product.salePrice === undefined
       ? ''
       : String(product.salePrice)
   );
    const formatDate = (value) => {
      if (!value) return '';
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return '';
      return d.toISOString().split('T')[0];
    };
    setSaleStartAt(formatDate(product.saleStartAt));
    setSaleEndAt(formatDate(product.saleEndAt));
    setImage(product.image || "");

    // multi-image support
    if (product.images && product.images.length) {
      setImages(product.images);
    } else if (product.image) {
      setImages([product.image]);
    } else {
      setImages([]);
    }
    setImageCaptions(
      product.imageCaptions && typeof product.imageCaptions === 'object'
        ? { ...product.imageCaptions }
        : {}
    );

    setBrand(product.brand || "");
    setCategory(product.category || "");

    // 🔹 INITIAL INVENTORY LOGIC
    // Treat stockQty as shelf (storefront stock), inventoryQty as backroom.
    const shelf = Number(product.stockQty ?? 0);
    const backroom = Number(product.inventoryQty ?? 0);

    setStockQty(shelf);
    setInventoryQty(backroom);

    setDescription(product.description || "");

    // Auto-parts
    setSku(product.sku || "");
    setBarcode(product.barcode || "");
    setLocation(product.location || "");
    const incomingLocations = Array.isArray(product.inventoryLocations) ? product.inventoryLocations : [];
    if (incomingLocations.length) {
      setInventoryLocations(
        incomingLocations.map((loc) => ({ ...blankInventoryLocation(), ...loc, quantity: Number(loc?.quantity ?? 0) || 0 }))
      );
    } else if (product.barcode || product.location) {
      setInventoryLocations([
        {
          ...blankInventoryLocation(),
          barcode: product.barcode || "",
          room: product.location || "",
        },
      ]);
    } else {
      setInventoryLocations([blankInventoryLocation()]);
    }
    setPartNumber(product.partNumber || "");
    setOemNumbers(
      Array.isArray(product.oemNumbers)
        ? product.oemNumbers.join(", ")
        : product.oemNumbers || ""
    );
    setPosition(product.position || "none");
    setAxle(product.axle || "none");
    setMaterial(normalizeMaterial(product.material || ""));
    setCoating(
      isRotorOrDiscCategory(product.category)
        ? normalizeCoating(product.coating)
        : "Non-coated"
    );
    setPackageDimension(product.packageDimension || "");
    setPackageDimensionUnit(
      ["mm", "cm", "inch"].includes(product.packageDimensionUnit) ? product.packageDimensionUnit : "cm"
    );
    if (typeof product.productDimension === "string") {
      setProductDimensionFormat("custom");
      setProductDimensionCustomText(product.productDimension || "");
      setProductDimensionValues({ length: "", width: "", height: "", diameter: "", thickness: "" });
    } else if (product.productDimension && typeof product.productDimension === "object") {
      setProductDimensionFormat(product.productDimension.format || "length_width_height");
      setProductDimensionValues({
        length: product.productDimension?.values?.length ?? "",
        width: product.productDimension?.values?.width ?? "",
        height: product.productDimension?.values?.height ?? "",
        diameter: product.productDimension?.values?.diameter ?? "",
        thickness: product.productDimension?.values?.thickness ?? "",
      });
      setProductDimensionCustomText(product.productDimension.customText || "");
    } else {
      setProductDimensionFormat("length_width_height");
      setProductDimensionCustomText("");
      setProductDimensionValues({ length: "", width: "", height: "", diameter: "", thickness: "" });
    }
    setProductDimensionUnit(
      ["mm", "cm", "inch"].includes(product.productDimensionUnit || product.productDimension?.unit)
        ? (product.productDimension?.unit || product.productDimensionUnit)
        : "mm"
    );
    setPackageWeight(product.packageWeight || "");
    setPackageWeightUnit(
      ["kg", "lb"].includes(product.packageWeightUnit) ? product.packageWeightUnit : "kg"
    );
    setAttributesText(
      product.attributes
        ? JSON.stringify(
            Object.fromEntries(Object.entries(product.attributes)),
            null,
            0
          )
        : ""
    );
    setEngineOilAttributes(buildEngineOilAttributes({
      attributes: product.attributes ? Object.fromEntries(Object.entries(product.attributes)) : {},
      sku: product.sku,
      brand: product.brand,
    }));

    // Fitments (normalize to editor shape)
    setFitments(
   (product.fitments || []).map((f) => ({
     make: f.make || "",
     model: f.model || "",
     trim: f.trim || "",
     year: f.year === "" || f.year == null ? null : Number(f.year),
     engine: f.engine || "",
     notes: f.notes || "",
   }))
);

    setInterchangeables(
      (product.interchangeables || []).map((entry) => ({
        brandLabel: entry.brandLabel || "",
        partNumber: entry.partNumber || "",
      }))
    );

    setBrakeRotorSpecs({
      outerDiameterMm: product?.brakeRotorSpecs?.outerDiameterMm ?? "",
      outerDiameterIn: product?.brakeRotorSpecs?.outerDiameterIn ?? "",
      overallHeightMm: product?.brakeRotorSpecs?.overallHeightMm ?? "",
      nominalThicknessMm: product?.brakeRotorSpecs?.nominalThicknessMm ?? "",
      minimumThicknessMm: product?.brakeRotorSpecs?.minimumThicknessMm ?? "",
      centerBoreMm: product?.brakeRotorSpecs?.centerBoreMm ?? "",
      boltCircleDiameterMm: product?.brakeRotorSpecs?.boltCircleDiameterMm ?? "",
      manufacturerDimensionText: product?.brakeRotorSpecs?.manufacturerDimensionText || "",
      rustResistantCoated: product?.brakeRotorSpecs?.rustResistantCoated || "",
      coatingType: product?.brakeRotorSpecs?.coatingType || "",
      coatingTypeOther: product?.brakeRotorSpecs?.coatingTypeOther || "",
      studLugHoleQuantity: product?.brakeRotorSpecs?.studLugHoleQuantity ?? "",
      rotorConstruction: product?.brakeRotorSpecs?.rotorConstruction || "",
      drivetrainType: product?.brakeRotorSpecs?.drivetrainType || "",
      rotorSizeLabel: product?.brakeRotorSpecs?.rotorSizeLabel || "",
    });

    setBrakePadSpecs({
      drivetrainType: product?.brakePadSpecs?.drivetrainType || "",
      padWearSensorIncluded: product?.brakePadSpecs?.padWearSensorIncluded || "",
      mountingHardwareIncluded: product?.brakePadSpecs?.mountingHardwareIncluded || "",
      brakeLubricantIncluded: product?.brakePadSpecs?.brakeLubricantIncluded || "",
    });
    setBrakeSpecType(product?.brakeSpecType || "");

  }, [product]);

  useEffect(() => {
    if (!isRotorOrDiscCategory(category)) return;
    setCoating((prev) => normalizeCoating(prev));
  }, [category]);
  useEffect(() => {
    if (!isNonCoated(coating)) return;
    setBrakeRotorSpecs((prev) => ({
      ...prev,
      coatingType: "",
      coatingTypeOther: "",
      rustResistantCoated: "No",
    }));
  }, [coating]);

  const effectiveBrakeSpecType = useMemo(() => {
    if (brakeSpecType === "pad" || brakeSpecType === "rotor" || brakeSpecType === "none") return brakeSpecType;
    if (isBrakePadProduct({ category, title, sku })) return "pad";
    if (isBrakeRotorProduct({ category, title, sku })) return "rotor";
    return "none";
  }, [brakeSpecType, category, title, sku]);
  const showRotorSection = effectiveBrakeSpecType === "rotor";
  const showPadSection = effectiveBrakeSpecType === "pad";
  const isEngineOil = isEngineOilCategory(category);

  const getProductPayload = (overrideFields = {}) => ({
    _id: productId,
    name: title,
    title,
    price: Number(price),
    basePrice: basePrice === "" ? null : Number(basePrice),
    status,
    salePrice: salePrice === '' ? null : Number(salePrice),
    saleStartAt: saleStartAt || null,
    saleEndAt: saleEndAt || null,
    image,
    images,
    imageCaptions,
    brand,
    category,
    stockQty,
    inventoryQty,
    description,
    brakeSpecType,
    sku,
    barcode,
    location,
    inventoryLocations,
    partNumber,
    oemNumbers: parseOemNumbers(oemNumbers),
    position,
    axle,
    material: normalizeMaterial(material),
    coating: isRotorOrDiscCategory(category) ? normalizeCoating(coating) : "",
    packageDimension,
    packageDimensionUnit,
    productDimension:
      productDimensionFormat === "custom" && productDimensionCustomText
        ? {
            format: "custom",
            values: {},
            unit: productDimensionUnit || "mm",
            customText: productDimensionCustomText,
          }
        : {
            format: productDimensionFormat,
            values: {
              length: productDimensionValues.length,
              width: productDimensionValues.width,
              height: productDimensionValues.height,
              diameter: productDimensionValues.diameter,
              thickness: productDimensionValues.thickness,
            },
            unit: productDimensionUnit || "mm",
            customText: productDimensionCustomText,
          },
    productDimensionUnit,
    packageWeight,
    packageWeightUnit,
    attributes: isEngineOil
      ? { ...parseAttributes(attributesText), ...engineOilAttributes }
      : parseAttributes(attributesText),
    fitments: (fitments || []).map((f) => ({
      make: f.make,
      model: f.model,
      trim: f.trim || "",
      year: Number(f.year) || null,
      engine: f.engine || "",
      notes: f.notes || "",
 })),
    brakeRotorSpecs: {
      outerDiameterMm: brakeRotorSpecs.outerDiameterMm === "" ? null : Math.max(0, Number(brakeRotorSpecs.outerDiameterMm)),
      outerDiameterIn: brakeRotorSpecs.outerDiameterIn === "" ? null : Math.max(0, Number(brakeRotorSpecs.outerDiameterIn)),
      overallHeightMm: brakeRotorSpecs.overallHeightMm === "" ? null : Math.max(0, Number(brakeRotorSpecs.overallHeightMm)),
      nominalThicknessMm: brakeRotorSpecs.nominalThicknessMm === "" ? null : Math.max(0, Number(brakeRotorSpecs.nominalThicknessMm)),
      minimumThicknessMm: brakeRotorSpecs.minimumThicknessMm === "" ? null : Math.max(0, Number(brakeRotorSpecs.minimumThicknessMm)),
      centerBoreMm: brakeRotorSpecs.centerBoreMm === "" ? null : Math.max(0, Number(brakeRotorSpecs.centerBoreMm)),
      boltCircleDiameterMm: brakeRotorSpecs.boltCircleDiameterMm === "" ? null : Math.max(0, Number(brakeRotorSpecs.boltCircleDiameterMm)),
      manufacturerDimensionText: brakeRotorSpecs.manufacturerDimensionText || "",
      rustResistantCoated: brakeRotorSpecs.rustResistantCoated || "",
      coatingType: brakeRotorSpecs.coatingType || "",
      coatingTypeOther: brakeRotorSpecs.coatingTypeOther || "",
      studLugHoleQuantity: brakeRotorSpecs.studLugHoleQuantity === "" ? null : Math.max(0, Number(brakeRotorSpecs.studLugHoleQuantity)),
      rotorConstruction: brakeRotorSpecs.rotorConstruction || "",
      drivetrainType: brakeRotorSpecs.drivetrainType || "",
      rotorSizeLabel: brakeRotorSpecs.rotorSizeLabel || "",
    },
    brakePadSpecs: {
      drivetrainType: brakePadSpecs.drivetrainType || "",
      padWearSensorIncluded: brakePadSpecs.padWearSensorIncluded || "",
      mountingHardwareIncluded: brakePadSpecs.mountingHardwareIncluded || "",
      brakeLubricantIncluded: brakePadSpecs.brakeLubricantIncluded || "",
    },
    interchangeables: (interchangeables || []).map((entry) => ({
      brandLabel: entry.brandLabel || "",
      partNumber: entry.partNumber || "",
    })),
    ...overrideFields, // This allows us to inject the NEW image URL
  });
  // ──────────────────────────────────────────────
  // Helpers
  const clampNonNeg = (v) => Math.max(0, Number(v) || 0);

  const parseOemNumbers = (txt) =>
    txt ? txt.split(",").map((s) => s.trim()).filter(Boolean) : [];

  const parseAttributes = (txt) => {
    if (!txt?.trim()) return {};
    try {
      const obj = JSON.parse(txt);
      if (obj && typeof obj === "object" && !Array.isArray(obj)) return obj;
    } catch {}
    // fallback: key=value;key2=value2
    const out = {};
    txt.split(";").forEach((pair) => {
      const [k, v] = pair.split("=");
      if (k && v) out[k.trim()] = v.trim();
    });
    return out;
  };

  const assignScannedBarcode = () => {
    const scanned = String(barcodeScanValue || "").trim();
    if (!scanned) {
      toast.error("Scan a barcode first.");
      return;
    }
    setBarcode(scanned);
    setInventoryLocations((prev) => {
      const next = [...prev];
      if (!next.length) next.push(blankInventoryLocation());
      next[0] = { ...next[0], barcode: scanned };
      return next;
    });
    setBarcodeScanValue("");
    toast.success("Barcode assigned to this product.");
  };

  // ──────────────────────────────────────────────
  // 🔹 INVENTORY LINKED LOGIC
  // Keep shelf and backroom values non-negative.

  const applyShelfChange = (newShelf) => {
    const shelf = clampNonNeg(newShelf);
    const backroom = clampNonNeg(inventoryQty);
    setStockQty(shelf);
    setInventoryQty(backroom);
  };

  const applyInventoryChange = (newBackroom) => {
    const backroom = clampNonNeg(newBackroom);
    const shelf = clampNonNeg(stockQty);
    setInventoryQty(backroom);
    setStockQty(shelf);
  };

  const handleStockChange = (e) => {
    applyShelfChange(e.target.value);
  };

  const handleInventoryChange = (e) => {
    applyInventoryChange(e.target.value);
  };

  const submitHandler = async (e) => {
    e.preventDefault();

    const payload = getProductPayload();

    try {
      const saved = await updateProduct(payload).unwrap();
      toast.success("Product updated");

      // reflect server truth immediately
      const serverShelf = clampNonNeg(saved.stockQty ?? 0);
      const serverBack = clampNonNeg(saved.inventoryQty ?? 0);

      setStockQty(serverShelf);
      setInventoryQty(serverBack);

      // sync other fields if the server changed them
      setTitle(saved.title ?? title);
      setPrice(saved.price ?? price);
      setBasePrice(saved.basePrice === null || saved.basePrice === undefined ? "" : String(saved.basePrice));
      setStatus(saved.status || status || 'active');
      setImage(saved.image ?? image);
      setImageCaptions(
        saved.imageCaptions && typeof saved.imageCaptions === 'object'
          ? { ...saved.imageCaptions }
          : imageCaptions
      );
      setBrand(saved.brand ?? brand);
      setCategory(saved.category ?? category);
      setDescription(saved.description ?? description);
      setPackageDimension(saved.packageDimension ?? packageDimension);
      setPackageDimensionUnit(saved.packageDimensionUnit ?? packageDimensionUnit);
      setProductDimensionUnit(saved.productDimensionUnit ?? productDimensionUnit);
      setPackageWeight(saved.packageWeight ?? packageWeight);
      setPackageWeightUnit(saved.packageWeightUnit ?? packageWeightUnit);
      setOemNumbers(
        Array.isArray(saved.oemNumbers)
          ? saved.oemNumbers.join(", ")
          : saved.oemNumbers || oemNumbers
      );
      setBarcode(saved.barcode ?? barcode);
      setLocation(saved.location ?? location);
      setInventoryLocations(
        Array.isArray(saved.inventoryLocations) && saved.inventoryLocations.length
          ? saved.inventoryLocations
          : [blankInventoryLocation()]
      );
      setInterchangeables(
        (saved.interchangeables || []).map((entry) => ({
          brandLabel: entry.brandLabel || "",
          partNumber: entry.partNumber || "",
        }))
      );

      // keep salePrice in sync with server
     if (saved.salePrice === null || saved.salePrice === undefined) {
       setSalePrice('');
     } else {
       setSalePrice(String(saved.salePrice));
     }
      const formatDate = (value) => {
        if (!value) return '';
        const d = new Date(value);
        if (Number.isNaN(d.getTime())) return '';
        return d.toISOString().split('T')[0];
      };
      setSaleStartAt(formatDate(saved.saleStartAt ?? saleStartAt));
      setSaleEndAt(formatDate(saved.saleEndAt ?? saleEndAt));

      // Optionally refetch to refresh any computed/derived server fields
      // await refetch();
    } catch (err) {
      toast.error(err?.data?.message || err?.error || "Update failed");
    }
  };
  const updateInventoryLocationRow = (idx, key, value) => {
    setInventoryLocations((prev) =>
      prev.map((row, rowIdx) =>
        rowIdx === idx ? { ...row, [key]: key === "quantity" ? (value === "" ? "" : Number(value) || 0) : value } : row
      )
    );
  };
  const addInventoryLocationRow = () => setInventoryLocations((prev) => [...prev, blankInventoryLocation()]);
  const removeInventoryLocationRow = (idx) =>
    setInventoryLocations((prev) => (prev.length <= 1 ? [blankInventoryLocation()] : prev.filter((_, rowIdx) => rowIdx !== idx)));
  const totalShelfQty = (inventoryLocations || []).reduce(
    (sum, row) => sum + (Number(row?.quantity) || 0),
    0
  );
  const expectedPhysicalQty = Number(inventoryQty || 0) + Number(stockQty || 0);
  const shelfQtyMismatch = totalShelfQty !== expectedPhysicalQty;
  const syncInventoryFromShelf = () => {
    const nextInventoryQty = Math.max(0, totalShelfQty - Number(stockQty || 0));
    setInventoryQty(nextInventoryQty);
    toast.success("Inventory Qty synced from Total Shelf Qty - Stock Qty.");
  };

 const uploadFileHandler = async (e) => {
    const formData = new FormData();
    formData.append("image", e.target.files[0]);
    
    try {
      // 1. Send file to backend
      const res = await uploadProductImage(formData).unwrap();
      toast.success(res.message || "Image uploaded");
      
      // 2. FIXED: Match the property name sent from Node.js (imageUrl)
      const url = res.imageUrl; 
      
      // 3. Update the Main Image state
      setImage(url); 

      // 4. Update the Gallery Images state
      setImages((prev) => {
        const next = prev ? [...prev] : [];
        if (!next.includes(url)) next.push(url);
        return next;
      });
      
    } catch (err) {
      toast.error(err?.data?.message || err.error || "Upload failed");
    }
  };

const handleRemoveImage = async (imageToRemove) => {
    if (!window.confirm("Delete this image permanently?")) return;

    try {
      // 1. Delete from S3
      await deleteImage({ image: imageToRemove }).unwrap();

      // 2. Calculate the new Gallery list
      const updatedImages = images.filter((img) => img !== imageToRemove);
      const { [imageToRemove]: _removedCaption, ...remainingCaptions } = imageCaptions;
      
      // 3. Calculate the new Main Image
      let updatedMainImage = image;

      // Check if we just deleted the current "Main" image
      if (image === imageToRemove) {
        if (updatedImages.length > 0) {
          // OPTION A: Promote the first remaining image to be Main
          updatedMainImage = updatedImages[0];
        } else {
          // OPTION B: No images left, so Main is empty
          updatedMainImage = '';
        }
      } 
      // Safety check: If gallery is empty, main must be empty
      else if (updatedImages.length === 0) {
        updatedMainImage = '';
      }

      await updateProduct(getProductPayload({ 
        image: updatedMainImage, 
        images: updatedImages,
        imageCaptions: remainingCaptions,
      })).unwrap();

      // 5. Update UI
      setImages(updatedImages);
      setImage(updatedMainImage);
      setImageCaptions(remainingCaptions);

      toast.success("Image deleted");
    } catch (err) {
      toast.error(err?.data?.message || "Delete failed");
    }
  };

  const handleCaptionChange = (img, value) => {
    setImageCaptions((prev) => ({ ...prev, [img]: value }));
  };

  const moveImage = (from, to) => {
    if (to < 0 || to >= images.length) return;
    setImages((prev) => {
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
  };

  const handleSetMainImage = (img) => {
    setImage(img);
  };

  const addInterchangeable = () => {
    setInterchangeables((prev) => [
      ...(prev || []),
      { brandLabel: "", partNumber: "" },
    ]);
  };

  const updateInterchangeable = (index, key, value) => {
    setInterchangeables((prev) =>
      (prev || []).map((entry, idx) =>
        idx === index ? { ...entry, [key]: value } : entry
      )
    );
  };

  const removeInterchangeable = (index) => {
    setInterchangeables((prev) => (prev || []).filter((_, idx) => idx !== index));
  };

  useEffect(() => {
    if (!tierPricesData?.tiers) return;

    setTierPriceRows(
      tierPricesData.tiers.map((row) => ({
        tierId: row.tierId,
        tierName: row.tierName,
        currency: row.currency || 'USD',
        price: row.price ?? '',
      }))
    );
    setTierPricesDirty(false);
  }, [tierPricesData]);

  const handleTierPriceChange = (tierId, value) => {
    setTierPricesDirty(true);
    setTierPriceRows((prev) =>
      prev.map((row) =>
        String(row.tierId) === String(tierId)
          ? { ...row, price: value === '' ? '' : Number(value) }
          : row
      )
    );
  };

  const handleTierCurrencyChange = (tierId, value) => {
    setTierPricesDirty(true);
    setTierPriceRows((prev) =>
      prev.map((row) =>
        String(row.tierId) === String(tierId)
          ? { ...row, currency: value || 'USD' }
          : row
      )
    );
  };

  const saveTierPricesHandler = async () => {
    const rows = tierPriceRows
      .filter((row) => row.price !== '' && Number(row.price) >= 0)
      .map((row) => ({
        tierId: row.tierId,
        price: Number(row.price),
        currency: row.currency || 'USD',
      }));

    if (!rows.length) {
      toast.error('Please enter at least one valid tier price.');
      return;
    }

    try {
      await upsertTierPrices({ productId, prices: rows }).unwrap();
      toast.success('Tier prices saved.');
      setTierPricesDirty(false);
    } catch (err) {
      toast.error(err?.data?.message || err?.error || 'Failed to save tier prices');
    }
  };

  return (
    <>
      <BackButton />

      <FormContainer colProps={{ xs: 12, lg: 11, xl: 12 }}>
        {loadingUpdate && <Loader />}

        {isLoading ? (
          <Loader />
        ) : error ? (
          <Message variant="danger">
            {error?.data?.message || error.error || "Error loading product"}
          </Message>
        ) : (
          <Form onSubmit={submitHandler}>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h1 className="mb-0">Edit Product</h1>
              <Button
                type="submit"
                variant="primary"
                disabled={loadingUpdate}
              >
                {loadingUpdate ? "Saving…" : "Apply"}
              </Button>
            </div>
            <Nav
              variant="tabs"
              activeKey={activeEditorTab}
              onSelect={(selectedKey) => setActiveEditorTab(selectedKey || "details")}
              className="mb-3"
            >
              <Nav.Item>
                <Nav.Link eventKey="details">Product Details</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="fitments">Fitments</Nav.Link>
              </Nav.Item>
            </Nav>

            {activeEditorTab === "details" && (
              <>
            {/* Core fields */}
            <Form.Group controlId="title" className="my-2">
              <Form.Label>Title</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter storefront title (customer-facing)"
                value={title}
                onChange={(e) => {
                  const nextTitle = e.target.value;
                  setTitle(nextTitle);
                }}
              />
              <Form.Text className="text-muted">
                This title is shown on the storefront. SKU is not used for customer-facing naming.
              </Form.Text>
            </Form.Group>

            <Row className="g-2">
              <Col md={6}>
                <Form.Group controlId="status" className="my-2">
                  <Form.Label>Product Status</Form.Label>
                  <Form.Select value={status} onChange={(e) => setStatus(e.target.value)}>
                    {PRODUCT_STATUS_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </Form.Select>
                  <Form.Text className="text-muted">
                    Draft is admin-only; Coming Soon is visible but cannot be purchased.
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
            <Form.Group controlId="price" className="my-2">
              <Form.Label>Price</Form.Label>
              <Form.Control
                type="number"
                placeholder="Enter price"
                value={price}
                onChange={(e) => setPrice(Number(e.target.value))}
              />
            </Form.Group>
              </Col>
            </Row>

            <Form.Group controlId="basePrice" className="my-2">
              <Form.Label>Base Price</Form.Label>
              <Form.Control
                type="number"
                min="0"
                step="0.01"
                placeholder="Leave blank"
                value={basePrice}
                onChange={(e) => setBasePrice(e.target.value)}
              />
              <Form.Text muted>
                Internal landed product cost. Used for future pricing calculations and not shown to customers.
              </Form.Text>
            </Form.Group>

            <Card className="my-3">
              <Card.Header className="d-flex justify-content-between align-items-center">
                <div>
                  <strong>Tier Pricing (Manual Override)</strong>
                  <div className="text-muted small">Set per-tier prices for this product.</div>
                </div>
                <Button
                  type="button"
                  variant="outline-primary"
                  size="sm"
                  onClick={saveTierPricesHandler}
                  disabled={savingTierPrices || loadingTierPrices || !tierPricesDirty}
                >
                  {savingTierPrices ? 'Saving…' : 'Save Tier Prices'}
                </Button>
              </Card.Header>
              <Card.Body>
                {loadingTierPrices ? (
                  <Loader />
                ) : tierPricesError ? (
                  <Message variant="danger">
                    {tierPricesError?.data?.message || tierPricesError?.error || 'Failed to load tier prices'}
                  </Message>
                ) : (
                  <Table striped bordered hover responsive size="sm" className="mb-0 align-middle">
                    <thead>
                      <tr>
                        <th>Tier</th>
                        <th style={{ minWidth: 140 }}>Manual Price</th>
                        <th style={{ minWidth: 120 }}>Currency</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tierPriceRows.length === 0 ? (
                        <tr>
                          <td colSpan={3} className="text-muted">No active tiers found.</td>
                        </tr>
                      ) : (
                        tierPriceRows.map((row) => (
                          <tr key={row.tierId}>
                            <td>{row.tierName}</td>
                            <td>
                              <Form.Control
                                type="number"
                                min="0"
                                step="0.01"
                                value={row.price}
                                placeholder="Leave blank = no override"
                                onChange={(e) => handleTierPriceChange(row.tierId, e.target.value)}
                              />
                            </td>
                            <td>
                              <Form.Control
                                type="text"
                                maxLength={5}
                                value={row.currency || 'USD'}
                                onChange={(e) => handleTierCurrencyChange(row.tierId, e.target.value.toUpperCase())}
                              />
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </Table>
                )}
              </Card.Body>
            </Card>

            <Form.Group controlId='salePrice' className='my-2'>
              <Form.Label>Sale Price (optional)</Form.Label>
              <Form.Control
                type='number'
                step='0.01'
                placeholder='Leave empty = no sale'
                value={salePrice}
                onChange={(e) => setSalePrice(e.target.value)}
              ></Form.Control>
            </Form.Group>

            <Row className="g-2">
              <Col md={6}>
                <Form.Group controlId="saleStartAt" className="my-2">
                  <Form.Label>Sale Start Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={saleStartAt}
                    onChange={(e) => setSaleStartAt(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group controlId="saleEndAt" className="my-2">
                  <Form.Label>Sale End Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={saleEndAt}
                    onChange={(e) => setSaleEndAt(e.target.value)}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group controlId="image" className="my-2">
              <Form.Label>Images</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter main image URL"
                value={image}
                onChange={(e) => setImage(e.target.value)}
              />
              <Form.Control
                type="file"
                className="mt-2"
                onChange={uploadFileHandler}
              />
              {loadingUpload && <Loader />}

              {images && images.length > 0 && (
                <div className="mt-2">
                  <strong>All Images</strong>
                  <ul
                    style={{
                      listStyle: "none",
                      paddingLeft: 0,
                      marginBottom: 0,
                    }}
                  >
                  {images.map((img, idx) => (
                    <li key={idx} className="d-flex align-items-center gap-2 mb-2 flex-wrap">
                      <img src={img} alt="thumb" style={{ width: 48, height: 48, objectFit: 'cover' }} />
                      <Form.Control
                        size="sm"
                        style={{ minWidth: 200, maxWidth: 320 }}
                        placeholder="Image name (optional)"
                        value={imageCaptions?.[img] || ''}
                        onChange={(e) => handleCaptionChange(img, e.target.value)}
                      />
                      <div className="d-flex gap-1 align-items-center flex-wrap">
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          onClick={() => moveImage(idx, idx - 1)}
                          disabled={idx === 0}
                        >
                          ↑
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          onClick={() => moveImage(idx, idx + 1)}
                          disabled={idx === images.length - 1}
                        >
                          ↓
                        </Button>
                        <Button
                          size="sm"
                          variant={image === img ? "success" : "outline-success"}
                          onClick={() => handleSetMainImage(img)}
                        >
                          {image === img ? "Main" : "Set main"}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-danger"
                          onClick={() => handleRemoveImage(img)}
                          disabled={loadingDelete}
                        >
                          Remove
                        </Button>
                      </div>
                    </li>
                    ))}
                  </ul>
                </div>
              )}
            </Form.Group>

            <Form.Group controlId="brand" className="my-2">
              <Form.Label>Brand</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter brand"
                value={brand}
                onChange={(e) => setBrand(e.target.value)}
              />
            </Form.Group>

            <Row>
              <Col md={6}>
                <Form.Group controlId="stockQty" className="my-3">
                  <Form.Label>Stock Qty (shelf)</Form.Label>
                  <Form.Control
                    type="number"
                    value={stockQty}
                    onChange={handleStockChange}
                  />
                  <Form.Text muted>Used by storefront availability.</Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group controlId="inventoryQty" className="my-3">
                  <Form.Label>Inventory Qty (backroom)</Form.Label>
                  <Form.Control
                    type="number"
                    value={inventoryQty}
                    onChange={handleInventoryChange}
                  />
                  <Form.Text muted>Used for backroom inventory.</Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group controlId="category" className="my-2">
              <Form.Label>Category</Form.Label>
              <Form.Select
                value={categorySelectValue}
                onChange={(e) => setCategory(e.target.value)}
                disabled={loadingCategories}
              >
                <option value="">
                  {loadingCategories
                    ? "Loading categories..."
                    : "Select category"}
                </option>
                {categoryOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Select>
              {categoriesError && (
                <Form.Text className="text-danger">
                  Unable to load categories.
                </Form.Text>
              )}
            </Form.Group>

            <Form.Group controlId="description" className="my-2">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="Enter description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </Form.Group>

            {/* Auto-parts fields */}
            <Form.Group className="my-2" controlId="sku">
              <Form.Label>SKU</Form.Label>
              <Form.Control
                type="text"
                value={sku}
                onChange={(e) => setSku(e.target.value)}
              />
            </Form.Group>

            <Form.Group className="my-2" controlId="barcode">
              <Form.Label>Barcode (Manufacturer/OEM)</Form.Label>
              <Row className="g-2 mb-2">
                <Col md={8}>
                  <Form.Control
                    ref={barcodeScanInputRef}
                    type="text"
                    value={barcodeScanValue}
                    placeholder="Click here and scan barcode"
                    onChange={(e) => setBarcodeScanValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        assignScannedBarcode();
                      }
                    }}
                  />
                </Col>
                <Col md={4} className="d-flex gap-2">
                  <Button
                    type="button"
                    variant="outline-secondary"
                    onClick={() => barcodeScanInputRef.current?.focus?.()}
                  >
                    Focus scanner
                  </Button>
                  <Button
                    type="button"
                    variant="outline-primary"
                    onClick={assignScannedBarcode}
                  >
                    Assign
                  </Button>
                </Col>
              </Row>
              <Form.Control
                type="text"
                value={barcode}
                onChange={(e) => setBarcode(e.target.value)}
              />
              <Form.Text muted>
                Scan and press Enter (or click Assign) to copy into this field. Barcodes can be shared by legacy products; SKU remains the unique internal identifier.
              </Form.Text>
            </Form.Group>

            <Form.Group className="my-2" controlId="inventoryLocations">
              <div className="d-flex justify-content-between align-items-center">
                <Form.Label className="mb-0">Inventory Locations</Form.Label>
                <Button type="button" size="sm" variant="outline-primary" onClick={addInventoryLocationRow}>Add row</Button>
              </div>
              {(inventoryLocations || []).map((row, idx) => (
                <Card key={`loc-${idx}`} className="mt-2 p-2">
                  <Row className="g-2">
                    <Col md={5}><Form.Control placeholder="Barcode" value={row.barcode || ""} onChange={(e) => updateInventoryLocationRow(idx, "barcode", e.target.value)} /></Col>
                    <Col md={4}><Form.Control placeholder="Location code (e.g. KT-S01-C03)" value={row.room || ""} onChange={(e) => updateInventoryLocationRow(idx, "room", e.target.value)} /></Col>
                    <Col md={2}><Form.Control type="number" min="0" placeholder="Shelf Qty" value={row.quantity ?? ""} onChange={(e) => updateInventoryLocationRow(idx, "quantity", e.target.value)} /></Col>
                    <Col md={1}><Button type="button" variant="outline-danger" size="sm" onClick={() => removeInventoryLocationRow(idx)}>Remove</Button></Col>
                  </Row>
                </Card>
              ))}
              <Form.Text muted>
                Each barcode should belong to one location row. Legacy barcode/location fields are kept for backward compatibility.
              </Form.Text>
              <WarehouseLocationBuilder
                currentLocation={location}
                onApply={(nextLocation) => {
                  setLocation(nextLocation);
                  setInventoryLocations((prev) => {
                    const next = Array.isArray(prev) ? [...prev] : [];
                    const hasBlankLastRow =
                      next.length > 0 &&
                      !String(next[next.length - 1]?.barcode || '').trim() &&
                      !String(next[next.length - 1]?.room || '').trim();
                    if (hasBlankLastRow) {
                      next[next.length - 1] = { ...next[next.length - 1], room: nextLocation };
                      return next;
                    }
                    return [...next, { ...blankInventoryLocation(), room: nextLocation }];
                  });
                }}
              />
              <div className="mt-3 small">
                <div><strong>Total Shelf Qty:</strong> {totalShelfQty}</div>
                <div><strong>Inventory Qty:</strong> {Number(inventoryQty || 0)}</div>
                <div><strong>Stock Qty:</strong> {Number(stockQty || 0)}</div>
                <div><strong>Expected Physical Qty:</strong> {expectedPhysicalQty}</div>
                {shelfQtyMismatch && (
                  <Message variant="warning">
                    Total Shelf Qty does not match Inventory Qty + Stock Qty.
                  </Message>
                )}
                <Button type="button" size="sm" variant="outline-secondary" onClick={syncInventoryFromShelf}>
                  Sync Inventory Qty from Shelf Qty and Stock Qty
                </Button>
              </div>
            </Form.Group>

            <Form.Group className="my-2" controlId="partNumber">
              <Form.Label>Manufacturer Part Number</Form.Label>
              <Form.Control
                type="text"
                value={partNumber}
                onChange={(e) => setPartNumber(e.target.value)}
              />
            </Form.Group>

            {!isEngineOil && (
              <>
            <Form.Group className="my-2" controlId="oemNumbers">
              <Form.Label>OEM Cross-References (comma-separated)</Form.Label>
              <Form.Control
                type="text"
                value={oemNumbers}
                onChange={(e) => setOemNumbers(e.target.value)}
                placeholder="04465-0R010, 04465-42170"
              />
            </Form.Group>

            <Row>
              <Col md={4}>
                <Form.Group className="my-2" controlId="position">
                  <Form.Label>Position</Form.Label>
                  <Form.Select
                    value={position}
                    onChange={(e) => setPosition(e.target.value)}
                  >
                    <option value="none">None</option>
                    <option value="front">Front</option>
                    <option value="rear">Rear</option>
                    <option value="left">Left</option>
                    <option value="right">Right</option>
                    <option value="front-left">Front-Left</option>
                    <option value="front-right">Front-Right</option>
                    <option value="rear-left">Rear-Left</option>
                    <option value="rear-right">Rear-Right</option>
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group className="my-2" controlId="axle">
                  <Form.Label>Axle</Form.Label>
                  <Form.Select
                    value={axle}
                    onChange={(e) => setAxle(e.target.value)}
                  >
                    <option value="none">None</option>
                    <option value="front">Front</option>
                    <option value="rear">Rear</option>
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group className="my-2" controlId="material">
                  <Form.Label>Material</Form.Label>
                  <Form.Select
                    value={material}
                    onChange={(e) => setMaterial(e.target.value)}
                  >
                    {MATERIAL_OPTIONS.map((option) => (
                      <option key={option || "blank"} value={option}>
                        {option || "Select material"}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
              </>
            )}
            <Row>
              <Col md={isEngineOil ? 6 : 4}>
                <Form.Group className="my-2" controlId="packageDimension">
                  <Form.Label>Package Dimension</Form.Label>
                  <div className="d-flex gap-2">
                    <Form.Control
                      type="text"
                      value={packageDimension}
                      onChange={(e) => setPackageDimension(e.target.value)}
                    />
                    <Form.Select
                      value={packageDimensionUnit}
                      onChange={(e) => setPackageDimensionUnit(e.target.value)}
                      style={{ maxWidth: 110 }}
                    >
                      <option value="mm">mm</option>
                      <option value="cm">cm</option>
                      <option value="inch">inch</option>
                    </Form.Select>
                  </div>
                </Form.Group>
              </Col>
              {!isEngineOil && <Col md={4}>
                <Form.Group className="my-2" controlId="productDimension">
                  <Form.Label>Product Dimension</Form.Label>
                  <div className="d-flex flex-column gap-2">
                    <Form.Select
                      value={productDimensionFormat}
                      onChange={(e) => setProductDimensionFormat(e.target.value)}
                    >
                      {PRODUCT_DIMENSION_FORMATS.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </Form.Select>
                    {productDimensionFormat === "custom" ? (
                      <Form.Control
                        type="text"
                        value={productDimensionCustomText}
                        onChange={(e) => setProductDimensionCustomText(e.target.value)}
                        placeholder="Custom Product Dimension Text"
                      />
                    ) : (
                      <div className="d-flex gap-2 flex-wrap">
                        {["length", "width", "height", "diameter", "thickness"]
                          .filter((key) => {
                            const map = {
                              length_width_height: ["length", "width", "height"],
                              diameter_height: ["diameter", "height"],
                              diameter_thickness: ["diameter", "thickness"],
                              length_width_thickness: ["length", "width", "thickness"],
                            };
                            return (map[productDimensionFormat] || []).includes(key);
                          })
                          .map((key) => (
                            <Form.Control
                              key={key}
                              type="number"
                              step="any"
                              placeholder={key.charAt(0).toUpperCase() + key.slice(1)}
                              value={productDimensionValues[key]}
                              onChange={(e) => setProductDimensionValues((prev) => ({ ...prev, [key]: e.target.value }))}
                              style={{ minWidth: 120, maxWidth: 170 }}
                            />
                          ))}
                      </div>
                    )}
                    <Form.Select
                      value={productDimensionUnit}
                      onChange={(e) => setProductDimensionUnit(e.target.value)}
                      style={{ maxWidth: 110 }}
                    >
                      <option value="mm">mm</option>
                      <option value="cm">cm</option>
                      <option value="inch">inch</option>
                    </Form.Select>
                  </div>
                </Form.Group>
              </Col>}
              <Col md={isEngineOil ? 6 : 4}>
                <Form.Group className="my-2" controlId="packageWeight">
                  <Form.Label>Package Weight</Form.Label>
                  <div className="d-flex gap-2">
                    <Form.Control
                      type="text"
                      value={packageWeight}
                      onChange={(e) => setPackageWeight(e.target.value)}
                    />
                    <Form.Select
                      value={packageWeightUnit}
                      onChange={(e) => setPackageWeightUnit(e.target.value)}
                      style={{ maxWidth: 110 }}
                    >
                      <option value="kg">kg</option>
                      <option value="lb">lb</option>
                    </Form.Select>
                  </div>
                </Form.Group>
              </Col>
            </Row>

            {isEngineOil ? (
              <EngineOilProductFields
                attributes={engineOilAttributes}
                onChange={(key, value) => setEngineOilAttributes((current) => ({ ...current, [key]: value }))}
              />
            ) : (
              <>
            {isRotorOrDiscCategory(category) && (
              <Form.Group className="my-2" controlId="coating">
                <Form.Label>Coating</Form.Label>
                <Form.Select
                  value={normalizeCoating(coating)}
                  onChange={(e) => setCoating(e.target.value)}
                >
                  {COATING_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            )}

            <Form.Group className="my-2">
              <Form.Label>Brake Spec Type</Form.Label>
              <Form.Select value={brakeSpecType} onChange={(e) => setBrakeSpecType(e.target.value)}>
                {BRAKE_SPEC_TYPE_OPTIONS.map((opt) => <option key={opt.value || "auto"} value={opt.value}>{opt.label}</option>)}
              </Form.Select>
            </Form.Group>

            {showRotorSection && <Card className="my-3 border-0 shadow-sm">
              <Card.Body>
                <h5 className="mb-1">Brake Rotor / Disc Details</h5>
                <Form.Text muted>
                  Fill these fields only when confirmed by manufacturer, OEM, or supplier data.
                </Form.Text>
                <Row className="mt-2">
                  <Col md={6}>
                    <Form.Group className="my-2">
                      <Form.Label>Rotor Outer Diameter mm</Form.Label>
                      <Form.Control type="number" min="0" step="any" value={brakeRotorSpecs.outerDiameterMm}
                        onChange={(e) => {
                          const raw = e.target.value;
                          if (raw === "") {
                            setBrakeRotorSpecs((prev) => ({ ...prev, outerDiameterMm: "", outerDiameterIn: "" }));
                            return;
                          }
                          const mm = Number(raw);
                          if (!Number.isFinite(mm) || mm < 0) return;
                          setBrakeRotorSpecs((prev) => ({ ...prev, outerDiameterMm: raw, outerDiameterIn: String((mm / 25.4).toFixed(2)) }));
                        }} />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="my-2">
                      <Form.Label>Rotor Outer Diameter inch</Form.Label>
                      <Form.Control type="number" min="0" step="any" value={brakeRotorSpecs.outerDiameterIn} readOnly />
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col md={4}><Form.Group className="my-2"><Form.Label>Rotor Overall Height mm</Form.Label><Form.Control type="number" min="0" step="any" value={brakeRotorSpecs.overallHeightMm} onChange={(e)=>setBrakeRotorSpecs((p)=>({...p,overallHeightMm:e.target.value}))}/></Form.Group></Col>
                  <Col md={4}><Form.Group className="my-2"><Form.Label>Nominal Thickness mm</Form.Label><Form.Control type="number" min="0" step="any" value={brakeRotorSpecs.nominalThicknessMm} onChange={(e)=>setBrakeRotorSpecs((p)=>({...p,nominalThicknessMm:e.target.value}))}/></Form.Group></Col>
                  <Col md={4}><Form.Group className="my-2"><Form.Label>Minimum Thickness mm</Form.Label><Form.Control type="number" min="0" step="any" value={brakeRotorSpecs.minimumThicknessMm} onChange={(e)=>setBrakeRotorSpecs((p)=>({...p,minimumThicknessMm:e.target.value}))}/></Form.Group></Col>
                </Row>
                <Row>
                  <Col md={4}><Form.Group className="my-2"><Form.Label>Center Bore / Hub Bore mm</Form.Label><Form.Control type="number" min="0" step="any" value={brakeRotorSpecs.centerBoreMm} onChange={(e)=>setBrakeRotorSpecs((p)=>({...p,centerBoreMm:e.target.value}))}/></Form.Group></Col>
                  <Col md={4}><Form.Group className="my-2"><Form.Label>Bolt Circle Diameter mm</Form.Label><Form.Control type="number" min="0" step="any" value={brakeRotorSpecs.boltCircleDiameterMm} onChange={(e)=>setBrakeRotorSpecs((p)=>({...p,boltCircleDiameterMm:e.target.value}))}/></Form.Group></Col>
                  <Col md={4}><Form.Group className="my-2"><Form.Label>Rotor Construction</Form.Label><Form.Select value={brakeRotorSpecs.rotorConstruction} onChange={(e)=>setBrakeRotorSpecs((p)=>({...p,rotorConstruction:e.target.value}))}>{ROTOR_CONSTRUCTION_OPTIONS.map((opt)=><option key={opt.value || "blank"} value={opt.value}>{opt.label}</option>)}</Form.Select></Form.Group></Col>
                </Row>
                <Form.Group className="my-2"><Form.Label>Manufacturer dimension text</Form.Label><Form.Control type="text" value={brakeRotorSpecs.manufacturerDimensionText} onChange={(e)=>setBrakeRotorSpecs((prev)=>({...prev, manufacturerDimensionText:e.target.value}))}/></Form.Group>
                <Row>
                  <Col md={4}><Form.Group className="my-2"><Form.Label>Rust resistant coated</Form.Label><Form.Select value={brakeRotorSpecs.rustResistantCoated} onChange={(e)=>setBrakeRotorSpecs((prev)=>({...prev, rustResistantCoated:e.target.value}))}><option value="">Select coating status</option>{YES_NO_OPTIONS.filter(Boolean).map((v)=><option key={v} value={v}>{v}</option>)}</Form.Select></Form.Group></Col>
                  <Col md={4}><Form.Group className="my-2"><Form.Label>Coating type</Form.Label><Form.Select disabled={isNonCoated(coating)} value={brakeRotorSpecs.coatingType} onChange={(e)=>setBrakeRotorSpecs((prev)=>({...prev, coatingType:e.target.value}))}><option value="">Select coating type</option>{ROTOR_COATING_TYPE_OPTIONS.filter(Boolean).map((v)=><option key={v} value={v}>{v}</option>)}</Form.Select></Form.Group></Col>
                  <Col md={4}><Form.Group className="my-2"><Form.Label>Stud/Lug Hole Quantity</Form.Label><Form.Control type="number" min="0" step="1" value={brakeRotorSpecs.studLugHoleQuantity} onChange={(e)=>setBrakeRotorSpecs((prev)=>({...prev, studLugHoleQuantity:e.target.value}))}/></Form.Group></Col>
                </Row>
                {!isNonCoated(coating) && brakeRotorSpecs.coatingType === "Other" && (<Form.Group className="my-2"><Form.Label>Coating type other</Form.Label><Form.Control type="text" value={brakeRotorSpecs.coatingTypeOther} onChange={(e)=>setBrakeRotorSpecs((prev)=>({...prev, coatingTypeOther:e.target.value}))}/></Form.Group>)}
                <Row>
                  <Col md={6}><Form.Group className="my-2"><Form.Label>Drivetrain Type</Form.Label><Form.Select value={brakeRotorSpecs.drivetrainType} onChange={(e)=>setBrakeRotorSpecs((prev)=>({...prev, drivetrainType:e.target.value}))}><option value="">Select drivetrain</option>{DRIVETRAIN_OPTIONS.filter(Boolean).map((v)=><option key={v} value={v}>{v}</option>)}</Form.Select></Form.Group></Col>
                  <Col md={6}><Form.Group className="my-2"><Form.Label>Rotor size label</Form.Label><Form.Control type="text" value={brakeRotorSpecs.rotorSizeLabel} onChange={(e)=>setBrakeRotorSpecs((prev)=>({...prev, rotorSizeLabel:e.target.value}))}/></Form.Group></Col>
                </Row>
              </Card.Body>
            </Card>}

            {showPadSection && <Card className="my-3 border-0 shadow-sm">
              <Card.Body>
                <h5 className="mb-1">Brake Pad Details</h5>
                <Form.Text muted>
                  Fill these fields only when confirmed by manufacturer, OEM, or supplier data.
                </Form.Text>
                <Row className="mt-2">
                  <Col md={4}><Form.Group className="my-2"><Form.Label>Drivetrain Type</Form.Label><Form.Select value={brakePadSpecs.drivetrainType} onChange={(e)=>setBrakePadSpecs((prev)=>({...prev, drivetrainType:e.target.value}))}><option value="">Select drivetrain</option>{DRIVETRAIN_OPTIONS.filter(Boolean).map((v)=><option key={v} value={v}>{v}</option>)}</Form.Select></Form.Group></Col>
                  <Col md={4}><Form.Group className="my-2"><Form.Label>Pad wear sensor included</Form.Label><Form.Select value={brakePadSpecs.padWearSensorIncluded} onChange={(e)=>setBrakePadSpecs((prev)=>({...prev, padWearSensorIncluded:e.target.value}))}><option value="">Select status</option>{YES_NO_OPTIONS.filter(Boolean).map((v)=><option key={v} value={v}>{v}</option>)}</Form.Select></Form.Group></Col>
                  <Col md={4}><Form.Group className="my-2"><Form.Label>Mounting hardware included</Form.Label><Form.Select value={brakePadSpecs.mountingHardwareIncluded} onChange={(e)=>setBrakePadSpecs((prev)=>({...prev, mountingHardwareIncluded:e.target.value}))}><option value="">Select status</option>{YES_NO_OPTIONS.filter(Boolean).map((v)=><option key={v} value={v}>{v}</option>)}</Form.Select></Form.Group></Col>
                </Row>
                <Row>
                  <Col md={4}><Form.Group className="my-2"><Form.Label>Brake lubricant included</Form.Label><Form.Select value={brakePadSpecs.brakeLubricantIncluded} onChange={(e)=>setBrakePadSpecs((prev)=>({...prev, brakeLubricantIncluded:e.target.value}))}><option value="">Select status</option>{YES_NO_OPTIONS.filter(Boolean).map((v)=><option key={v} value={v}>{v}</option>)}</Form.Select></Form.Group></Col>
                </Row>
              </Card.Body>
            </Card>}
            <Form.Group className="my-2" controlId="attributes">
              <Form.Label>Attributes (JSON or key=value;key2=value2)</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={attributesText}
                onChange={(e) => setAttributesText(e.target.value)}
                placeholder='{"padThickness":"16mm","rotorDia":"295mm"}'
              />
            </Form.Group>

            <div className="my-3">
              <div className="d-flex align-items-center justify-content-between mb-2">
                <h5 className="mb-0">Interchangeables</h5>
                <Button size="sm" variant="outline-primary" onClick={addInterchangeable}>
                  + Add Interchangeable
                </Button>
              </div>
              <Table bordered hover size="sm" responsive className="align-middle">
                <thead>
                  <tr>
                    <th>Brand Label</th>
                    <th>Part Number</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {(interchangeables || []).length === 0 ? (
                    <tr>
                      <td colSpan={3} className="text-muted">
                        No interchangeables yet.
                      </td>
                    </tr>
                  ) : (
                    (interchangeables || []).map((entry, idx) => (
                      <tr key={`interchangeable-${idx}`}>
                        <td>
                          <Form.Control
                            type="text"
                            value={entry.brandLabel}
                            onChange={(e) =>
                              updateInterchangeable(idx, "brandLabel", e.target.value)
                            }
                          />
                        </td>
                        <td>
                          <Form.Control
                            type="text"
                            value={entry.partNumber}
                            onChange={(e) =>
                              updateInterchangeable(idx, "partNumber", e.target.value)
                            }
                          />
                        </td>
                        <td className="text-end">
                          <Button
                            size="sm"
                            variant="outline-danger"
                            onClick={() => removeInterchangeable(idx)}
                          >
                            Remove
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </div>
              </>
            )}

            </>
            )}

            {!isEngineOil && activeEditorTab === "fitments" && (
              <div className="my-3">
                <Card className="shadow-sm border-0">
                  <Card.Body>
                    <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-3">
                      <div>
                        <Card.Title className="mb-1">Fitments</Card.Title>
                        <Card.Text className="text-muted small mb-0">
                          Build a clean compatibility matrix for this product. Changes are saved with the main product update.
                        </Card.Text>
                      </div>
                      <div className="small text-muted">
                        Tip: add broad fitments first (make/model), then refine with year, trim, and engine.
                      </div>
                    </div>
                    <FitmentEditor value={fitments} onChange={setFitments} />
                  </Card.Body>
                </Card>
              </div>
            )}

          </Form>
        )}
      </FormContainer>
    </>
  );
};

export default ProductEditScreen;
